/* Public Discovery pathway — anonymous search over the WHO provider directory.
   Clearly labeled: these are demonstration placeholder records until the real
   partner list is onboarded. */
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Filter, MapPin, ExternalLink, Info, Phone, CheckCircle2, Compass, Handshake } from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENT_CATEGORY = "veteran_entrepreneurship";
const SUBCATEGORY_LABELS = {
  entrepreneurship_training: "Entrepreneurship Exploration & Training",
  business_planning_formation: "Business Planning & Formation",
  business_acquisition: "Business Acquisition & Ownership Transitions",
  counseling_mentorship: "Business Counseling & Mentorship",
  financing_capital_readiness: "Startup Financing & Capital Readiness",
  veteran_certification: "Veteran-Owned Business Certification",
  government_contracting: "Government Contracting & Procurement",
  operations_growth: "Business Operations & Growth",
  technology_innovation: "Technology, Innovation & Commercialization",
  networking_ecosystems: "Business Networking & Entrepreneurial Ecosystems",
};

export default function Discovery() {
  const [searchParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [sub, setSub] = useState(searchParams.get("sub") || "");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leg, setLeg] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (category === ENT_CATEGORY && sub) params.sub = sub;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/discovery/providers`, { params });
      setResults(r.data.providers || []);
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [category, sub, q]);

  useEffect(() => {
    axios.get(`${API}/discovery/categories`).then((r) => {
      setCats(r.data.categories || []);
      setSubs(r.data.entrepreneurship_subcategories || []);
    });
    axios.get(`${API}/discovery/legislative`).then((r) => setLeg(r.data.resources || []));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-[70vh] bg-cream" data-testid="discovery-page">
      <Seo title="Discover services · WHO" path="/discovery" description="Search WHO's directory of veteran-serving organizations in Western Pennsylvania. Pilot demonstration — placeholder records labeled accordingly." />
      <PilotDemoBanner text="Directory records are prospective pilot resources. Inclusion in the WHO Discovery directory does not constitute a partnership, endorsement, referral agreement, or participation in WHO's coordinated services by the listed organization." />
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-12">
        <p className="eyebrow text-gold-deep">Discovery</p>
        <h1 className="mt-3 font-display font-black text-navy text-3xl lg:text-4xl">Find a verified-track service.</h1>
        <p className="mt-3 text-slateSoft max-w-2xl">
          Search WHO&rsquo;s directory of veteran-serving organizations in Western Pennsylvania. Each record will be verified
          and partnership-confirmed before any live coordination occurs.
        </p>

        {/* Controls */}
        <div className="mt-8 grid md:grid-cols-[1fr_280px_120px] gap-3" data-testid="discovery-controls">
          <label className="relative">
            <Search className="w-4 h-4 text-slateSoft absolute top-3.5 left-3 pointer-events-none" />
            <input
              placeholder="Search by name, description, or tag"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-3 py-3 bg-white border border-borderSubtle rounded-md focus:outline-none focus:border-gold"
              data-testid="discovery-search"
            />
          </label>
          <label className="relative">
            <Filter className="w-4 h-4 text-slateSoft absolute top-3.5 left-3 pointer-events-none" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full pl-9 pr-3 py-3 bg-white border border-borderSubtle rounded-md focus:outline-none focus:border-gold"
              data-testid="discovery-category"
            >
              <option value="">All categories</option>
              {cats.map((c) => (
                <option key={c} value={c}>
                  {c.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={load}
            className="bg-navy hover:bg-navy-deep text-cream font-semibold rounded-md"
            data-testid="discovery-search-btn"
          >
            Search
          </button>
        </div>

        <p className="mt-4 text-sm text-slateSoft" data-testid="discovery-count">
          {loading ? "Searching…" : `${total} result${total === 1 ? "" : "s"}`}
        </p>

        {/* Veteran Entrepreneurship & Business Ownership — category intro + subcategory filters */}
        {category === ENT_CATEGORY && (
          <div className="mt-6 p-6 bg-white border border-gold/40 rounded-lg" data-testid="discovery-entrepreneurship-intro">
            <p className="eyebrow text-gold-deep">Veteran Entrepreneurship &amp; Business Ownership</p>
            <p className="mt-3 text-slateSoft leading-relaxed">
              Explore resources that help veterans, transitioning service members, and military families start,
              acquire, manage, and grow businesses.
            </p>
            <p className="mt-3 text-sm text-slateSoft leading-relaxed">
              From an initial business idea to business formation, financing, certification, government contracting,
              and growth, veteran entrepreneurs may need assistance from several organizations and programs. WHO helps
              make these resources easier to discover and is designing a future coordinated-support approach intended
              to help veterans establish meaningful connections with appropriate service providers.
            </p>
            <p className="mt-3 text-xs text-navy/70 font-semibold">
              These are candidate resources, not confirmed WHO partners. Directory inclusion does not establish that an
              organization — or any of its individual programs — has agreed to participate in WHO&rsquo;s future
              coordinated-services network.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="discovery-subcategory-chips">
              <button
                onClick={() => setSub("")}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${!sub ? "bg-navy text-cream border-navy" : "bg-cream-panel text-navy/70 border-borderSubtle hover:border-gold"}`}
                data-testid="discovery-sub-all"
              >
                All entrepreneurship services
              </button>
              {subs.map((s) => (
                <button
                  key={s}
                  onClick={() => setSub(sub === s ? "" : s)}
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${sub === s ? "bg-navy text-cream border-navy" : "bg-cream-panel text-navy/70 border-borderSubtle hover:border-gold"}`}
                  data-testid={`discovery-sub-${s}`}
                >
                  {SUBCATEGORY_LABELS[s] || s.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <ul className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discovery-results">
          {results.map((p) => (
            <li key={p.id} className="p-5 bg-white border border-borderSubtle rounded-lg hover:border-gold/50 transition-colors" data-testid={`discovery-card-${p.id}`}>
              <p className="text-[10px] uppercase tracking-widest text-gold-deep font-bold">
                {(p.categories || []).slice(0, 2).join(" · ").replace(/_/g, " ")}
              </p>
              <h3 className="mt-1 font-display font-bold text-navy text-lg">{p.display_name}</h3>
              {p.organization_role === "legislative_office" && (
                <p className="mt-1 text-xs text-navy/70">
                  {[p.officeholder_name && `Officeholder: ${p.officeholder_name}${p.officeholder_party ? ` (${p.officeholder_party})` : ""}`, p.district_code].filter(Boolean).join(" · ")}
                </p>
              )}
              {p.service_areas?.length > 0 && (
                <p className="mt-1 text-xs text-slateSoft flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {p.service_areas.join(", ")}
                </p>
              )}
              {p.description && <p className="mt-3 text-sm text-slateSoft">{p.description}</p>}
              {p.subcategories?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`discovery-subs-${p.id}`}>
                  {p.subcategories.map((s) => (
                    <span key={s} className="text-[10px] font-semibold uppercase tracking-wide bg-navy/5 text-navy/70 border border-navy/10 rounded-full px-2 py-0.5">
                      {SUBCATEGORY_LABELS[s] || s.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              )}
              {p.programs?.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid={`discovery-programs-${p.id}`}>
                  {p.programs.map((prog) => (
                    <li key={prog.name} className="text-xs bg-cream-panel border border-borderSubtle rounded-md p-2.5">
                      <p className="font-semibold text-navy">{prog.name}</p>
                      {prog.description && <p className="mt-0.5 text-slateSoft">{prog.description}</p>}
                      {prog.eligibility && <p className="mt-0.5 text-navy/60"><span className="font-semibold">Eligibility:</span> {prog.eligibility}</p>}
                      {prog.url && (
                        <a href={prog.url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-navy underline underline-offset-2">
                          <ExternalLink className="w-3 h-3" /> Program page
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                {p.verification_status === "verified" ? (
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5 font-semibold" data-testid={`discovery-verified-${p.id}`}>
                    <CheckCircle2 className="w-3 h-3" /> Verified organization info
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-semibold" data-testid={`discovery-unverified-${p.id}`}>
                    <Info className="w-3 h-3" /> Unverified
                  </span>
                )}
                {p.organization_role === "legislative_office" && (
                  <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-800 border border-sky-200 rounded-full px-2 py-0.5 font-semibold">
                    Legislative office · Discovery only
                  </span>
                )}
                {p.partnership_status === "unconfirmed" && p.organization_role !== "legislative_office" && (
                  <span className="inline-flex items-center gap-1 bg-cream-panel border border-borderSubtle text-navy/70 rounded-full px-2 py-0.5">
                    <Info className="w-3 h-3" /> Partnership unconfirmed
                  </span>
                )}
                {p.demo && (
                  <span className="inline-flex items-center gap-1 bg-gold/15 text-gold-deep border border-gold/40 rounded-full px-2 py-0.5 font-semibold">
                    Demo record
                  </span>
                )}
                {p.phone && (
                  <span className="inline-flex items-center gap-1 text-slateSoft">
                    <Phone className="w-3 h-3" /> {p.phone}
                  </span>
                )}
                {p.website && (
                  <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-navy underline underline-offset-2">
                    <ExternalLink className="w-3 h-3" /> Website
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>

        {/* Legislative */}
        {leg.length > 0 && (
          <section className="mt-16" data-testid="discovery-legislative">
            <p className="eyebrow text-gold-deep">Government &amp; legislative</p>
            <h2 className="mt-3 font-display font-black text-navy text-2xl">Policy &amp; legislative resources</h2>
            <ul className="mt-5 grid md:grid-cols-2 gap-4">
              {leg.map((l) => (
                <li key={l.id} className="p-4 bg-white border border-borderSubtle rounded-lg">
                  <p className="text-[10px] uppercase tracking-widest text-gold-deep font-bold">{l.category.replace(/_/g, " ")}</p>
                  <h3 className="mt-1 font-display font-bold text-navy">{l.title}</h3>
                  {l.jurisdiction && <p className="text-xs text-slateSoft">{l.jurisdiction}</p>}
                  {l.description && <p className="mt-2 text-sm text-slateSoft">{l.description}</p>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Discovery vs. Coordinated Services — public explanation */}
        <section className="mt-16 grid md:grid-cols-2 gap-4" data-testid="discovery-pathways-explainer">
          <div className="p-6 bg-white border border-borderSubtle rounded-lg">
            <p className="eyebrow text-gold-deep flex items-center gap-2"><Compass className="w-4 h-4" /> Discovery</p>
            <p className="mt-3 text-sm text-slateSoft leading-relaxed">
              For veterans who want to explore business-development resources, entrepreneurship training,
              certification information, financing options, or networking opportunities. You can review
              public resource information here and contact organizations independently — no account needed.
            </p>
          </div>
          <div className="p-6 bg-navy-deep text-cream rounded-lg">
            <p className="eyebrow text-gold flex items-center gap-2"><Handshake className="w-4 h-4" /> Coordinated Services — Planned Capability</p>
            <p className="mt-3 text-sm text-cream/80 leading-relaxed">
              For veterans who need more active assistance connecting with service providers across multiple
              needs. WHO&rsquo;s intended architecture emphasizes permission, meaningful connection
              confirmation, follow-through, and recovery when a handoff does not succeed.
            </p>
            <p className="mt-3 text-[11px] text-cream/60">Informational only — coordinated services are a planned capability, not an active program.</p>
          </div>
        </section>

        <p className="mt-16 text-xs text-slateSoft" data-testid="discovery-disclaimer">
          Information in this directory is for discovery purposes only. Inclusion in the WHO Discovery
          directory does not constitute a partnership, endorsement, referral agreement, or participation
          in WHO&rsquo;s coordinated services by the listed organization. Verification confirms an
          organization&rsquo;s identity and public details against authoritative sources. Live
          warm-handoff coordination requires partnership verification and is not yet active.
        </p>
      </div>
    </div>
  );
}
